"use client";

import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Search,
  FileText,
  Calendar,
} from "lucide-react";
import { EventCard } from "@/app/events/event-card"
import { EventCountCard } from "@/app/events/event-count-card"
//import supabase from "@/config/supabaseclient"
import { useContext } from "react";
import { EventsContext } from "../context/eventsContext";
import { Header } from "@/components/header";

const eventTypes = [
  "All Types",
  "Conference",
  "Workshop",
  "Meeting",
  "Training",
  "Product Launch",
  "Seminar",
];
const statusOptions = [
  "All Status",
  "requested",
  "planned",
  "in progress",
  "completed",
  "cancelled",
  "approved",
];

/*const statusColors = {
  Requested: "bg-black-100 text-black-800 border-black-200",
  planned: "bg-yellow-100 text-yellow-800 border-yellow-200",
  "in-progress": "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};*/

const statusLabels = {
  Requested: "requested",
  planned: "planned",
  "in-progress": "in progress",
  completed: "completed",
  cancelled: "cancelled",
  approved: "approved",
};

export default function EventManagementPage() {

  // Access events from context
  const { events } = useContext(EventsContext);

  // State for search and filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [typeFilter, setTypeFilter] = useState("All Types");

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("status") === "requested") {
      setStatusFilter("requested");
    }
  }, []);

  // Filter events based on search and filters
  const filteredEvents = useMemo(() => {

    return events.filter((event) => {

        const matchesSearch =
          event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.description.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesStatus =
          statusFilter === "All Status" ||
          statusLabels[event.status as keyof typeof statusLabels] === statusFilter;

        const matchesType =
          typeFilter === "All Types" || event.type === typeFilter;
      
      return matchesSearch && matchesStatus && matchesType;

    });
  }, [events, searchQuery, statusFilter, typeFilter]);

  // Calculate past and future events
  /*const today = new Date();
  const pastEvents = filteredEvents.filter(
    (event) => event.endDate && new Date(event.endDate) < today
  );*/

  // Current events are those happening today
  /*const currentEvents = filteredEvents.filter(
    (event) =>
      event.startDate &&
      event.endDate &&
      new Date(event.startDate) <= today &&
      new Date(event.endDate) >= today
  );*/

  // Requested events are those with a status of "Requested"
  /*const requestedEvents = filteredEvents.filter(
    (event) => event.status === "Requested"
  );*/

  // Cancelled events are those with a status of "cancelled"
  const cancelledEvents = filteredEvents.filter(
    (event) => event.status === "cancelled"
  );
  
  // Approved events are those with a status of "planned"
  const approvedEvents = filteredEvents.filter(
    (event) => event.status === "planned"
  );

  // Upcoming events are those starting after today
  /*const futureEvents = filteredEvents.filter(
    (event) => event.startDate && new Date(event.startDate) > today
  );*/

// return events scheduled for today and 'Requested' state
const requestedEvents = events.filter((event) => {

  return event.status.toLowerCase().includes("requested")

})

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("All Status");
    setTypeFilter("All Types");
  };

  const hasActiveFilters =
    searchQuery || statusFilter !== "All Status" || typeFilter !== "All Types";

  return (
    <>
      {/*<Header title="Event Management" />*/}
      <Header title="Events" subtitle="Manage events and their details" />
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">

          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className="bg-transparent"
            >
              Clear Filters
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <EventCountCard title="Pending Approval" count={requestedEvents.length} colour="text-yellow-600" bgcolour="bg-yellow-100" icon={Calendar}/>
            <EventCountCard title="Approved" count={approvedEvents.length} colour="text-green-600" bgcolour="bg-green-100" icon={Calendar}/>
            <EventCountCard title="Cancelled" count={cancelledEvents.length} colour="text-red-600" bgcolour="bg-red-100" icon={Calendar}/>
          </div>

          {/* All Events Count card */}
          {/*<div className="grid grid-cols-1 md:grid-cols-5 gap-4">

            <EventCountCard 
            title="All Events" 
            count={filteredEvents.length} 
            colour="text-primary" 
            bgcolour="bg-primary/10" 
            icon={Calendar}/>
            
            <EventCountCard 
            title="Requested Events" 
            count={requestedEvents.length} 
            colour="text-red-600" 
            bgcolour="bg-red-100" 
            icon={Calendar}/>
            
            <EventCountCard 
            title="Completed Events" 
            count={completedEvents.length} 
            colour="text-grey-600" 
            bgcolour="bg-grey-100" 
            icon={Calendar}/> 
            
            <EventCountCard 
            title="Current Events" 
            count={activeEvents.length} 
            colour="text-green-600" 
            bgcolour="bg-green-100" 
            icon={Calendar}/>

            <EventCountCard 
            title="Future Events" 
            count={upcomingEvents.length} 
            colour="text-blue-600" 
            bgcolour="bg-blue-100" 
            icon={Calendar}/>
          </div>*/}

          {/* Directory Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Event Directory</h2>
              <p className="text-foreground" style={{ color: "grey" }}>
                {filteredEvents.length} events available
              </p>
            </div>
          </div>

          {/* Events Grid */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {filteredEvents.map((event) => (
              <EventCard
                key={event.id}
                {...event}
                startDate={
                  event.startDate instanceof Date
                    ? event.startDate.toISOString().split("T")[0]
                    : event.startDate || ""
                }
                endDate={
                  event.endDate instanceof Date
                    ? event.endDate.toISOString().split("T")[0]
                    : event.endDate || ""
                }
                nonResStartDate={
                  event.nonResStartDate instanceof Date
                    ? event.nonResStartDate.toISOString().split("T")[0]
                    : event.nonResStartDate?.toString() || ""
                }
                nonResEndDate={
                  event.nonResEndDate instanceof Date
                    ? event.nonResEndDate.toISOString().split("T")[0]
                    : event.nonResEndDate?.toString() || ""
                }
              />
            ))}
          </div>

          {filteredEvents.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No events found</h3>
              <p className="text-muted-foreground mb-6">
                {hasActiveFilters
                  ? "Try adjusting your filters or search terms"
                  : "Get started by creating your first event"}
              </p>
              {!hasActiveFilters && (
                <Button>Create Your First Event</Button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
